/**
 * ShadowNoteTestDialog
 *
 * Two-phase dialog triggered from the template detail page:
 *  1. **Trigger** — admin picks a provider → picks an encounter → fires POST
 *  2. **View** — polls the shadow note, renders sections as they arrive
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { MarkdownNoteSectionDisplayBox } from '@components/Note/MarkdownNoteSectionDisplayBox';
import { Science } from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { AdminEncounterResponse } from '@store/generated/admin';
import type { AdminOrganizationUserResponse } from '@store/generated/organizations';

import {
  useGetProviderEncountersQuery,
  useGetV2AdminOrganizationsByOrganizationIdQuery,
} from '../store/hooks';
import {
  useCreateShadowNoteMutation,
  useGetShadowNoteQuery,
} from '../store/shadowNotesApi';
import type { ShadowNoteSection } from '../types/shadow-notes';
import { formatDateTime, getStatusBadgeColor } from '../utils/formatting';

// ---------------------------------------------------------------------------
// Polling
// ---------------------------------------------------------------------------

const TERMINAL_STATUSES = new Set(['completed', 'error']);

function getPollingInterval(status: string | null | undefined): number | undefined {
  if (!status || TERMINAL_STATUSES.has(status)) return undefined;
  return 3000;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function encounterLabel(enc: AdminEncounterResponse): string {
  const date = enc.created_at ? formatDateTime(enc.created_at) : 'No date';
  const type = enc.encounter_type ?? 'Unknown type';
  const title = enc.note_title ? ` — ${enc.note_title}` : '';
  const status = enc.status ? ` [${enc.status}]` : '';
  return `${date} · ${type}${title}${status}`;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ShadowNoteTestDialogProps {
  open: boolean;
  onClose: () => void;
  templateId: string;
  templateTitle: string;
  templateVersion: number;
  /** Pre-filled if the template belongs to an org */
  organizationId?: string | null;
  /** Pre-filled if navigated from a provider context */
  providerId?: string | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ShadowNoteTestDialog({
  open,
  onClose,
  templateId,
  templateTitle,
  templateVersion,
  organizationId: prefilledOrgId,
  providerId: prefilledProviderId,
}: ShadowNoteTestDialogProps) {
  // ---- Form state (trigger phase) ----
  const [organizationId, setOrganizationId] = useState(prefilledOrgId ?? '');
  const [selectedProvider, setSelectedProvider] = useState<AdminOrganizationUserResponse | null>(null);
  const [selectedEncounter, setSelectedEncounter] = useState<AdminEncounterResponse | null>(null);

  // ---- Shadow note state (view phase) ----
  const [shadowNoteId, setShadowNoteId] = useState<string | null>(null);
  const [pollingInterval, setPollingInterval] = useState<number | undefined>(undefined);

  // ---- Data queries ----
  const { data: orgData, isLoading: orgLoading } = useGetV2AdminOrganizationsByOrganizationIdQuery(
    { organizationId },
    { skip: !organizationId },
  );

  const effectiveProviderId = selectedProvider?.user_id ?? prefilledProviderId ?? '';

  const { data: encountersData, isLoading: encountersLoading } = useGetProviderEncountersQuery(
    { providerId: effectiveProviderId, perPage: 50 },
    { skip: !effectiveProviderId },
  );

  const [createShadowNote, { isLoading: isCreating, error: createError }] =
    useCreateShadowNoteMutation();

  const { data: shadowNote } = useGetShadowNoteQuery(
    { shadowNoteId: shadowNoteId ?? '', organizationId },
    { skip: !shadowNoteId, pollingInterval },
  );

  // ---- Provider list (active users from org detail) ----
  const providers = useMemo(
    () => (orgData?.users ?? []).filter((u) => u.status === 'active'),
    [orgData],
  );

  // ---- Encounter list sorted by recency ----
  const encounters = useMemo(() => {
    const items = encountersData?.items ?? [];
    return [...items].sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
      return dateB - dateA;
    });
  }, [encountersData]);

  // ---- Sync prefilled values ----
  useEffect(() => {
    if (prefilledOrgId) setOrganizationId(prefilledOrgId);
  }, [prefilledOrgId]);

  // Auto-select provider if prefilledProviderId matches one in the list
  useEffect(() => {
    if (prefilledProviderId && providers.length > 0 && !selectedProvider) {
      const match = providers.find((p) => p.user_id === prefilledProviderId);
      if (match) setSelectedProvider(match);
    }
  }, [prefilledProviderId, providers, selectedProvider]);

  // Adjust polling based on status
  useEffect(() => {
    if (shadowNote?.status) {
      setPollingInterval(getPollingInterval(shadowNote.status));
    }
  }, [shadowNote?.status]);

  // ---- Handlers ----

  const handleTrigger = useCallback(async () => {
    if (!selectedEncounter) return;

    const result = await createShadowNote({
      createShadowNoteRequest: {
        encounter_id: selectedEncounter.encounter_id,
        note_template_id: templateId,
        organization_id: organizationId.trim(),
        created_by: 'admin',
        template_version: templateVersion,
      },
    }).unwrap();

    if (result.shadow_note_id) {
      setShadowNoteId(result.shadow_note_id);
      setPollingInterval(3000);
    }
  }, [createShadowNote, selectedEncounter, templateId, organizationId, templateVersion]);

  const handleClose = useCallback(() => {
    setShadowNoteId(null);
    setPollingInterval(undefined);
    setSelectedEncounter(null);
    onClose();
  }, [onClose]);

  // ---- Derived ----

  const isViewing = !!shadowNoteId;
  const status = shadowNote?.status ?? 'processing';
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- generated JSONB type
  const sections: ShadowNoteSection[] = shadowNote?.content?.sections ?? [];
  const canTrigger = selectedEncounter && organizationId.trim() && !isCreating;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={isViewing ? 'lg' : 'sm'}
      fullWidth
      aria-labelledby="shadow-note-test-title"
    >
      {/* ---- TRIGGER PHASE ---- */}
      {!isViewing && (
        <>
          <DialogTitle id="shadow-note-test-title">
            <Box display="flex" alignItems="center" gap={1}>
              <Science color="primary" />
              <Typography variant="h6">Test Template</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Generate a shadow note using <strong>{templateTitle}</strong> (v{templateVersion})
              against a real encounter transcript.
            </Typography>
          </DialogTitle>

          <DialogContent>
            <Stack spacing={2.5} sx={{ mt: 1 }}>
              {/* Org ID — editable only when not pre-filled */}
              <TextField
                label="Organization ID"
                value={organizationId}
                onChange={(e) => {
                  setOrganizationId(e.target.value);
                  setSelectedProvider(null);
                  setSelectedEncounter(null);
                }}
                placeholder="Paste org UUID"
                required
                fullWidth
                size="small"
                disabled={!!prefilledOrgId}
                helperText={prefilledOrgId ? 'Pre-filled from template' : undefined}
              />

              {/* Provider picker */}
              <Autocomplete
                options={providers}
                loading={orgLoading}
                value={selectedProvider}
                onChange={(_e, value) => {
                  setSelectedProvider(value);
                  setSelectedEncounter(null);
                }}
                getOptionLabel={(opt) => opt.name ?? opt.user_id}
                isOptionEqualToValue={(opt, val) => opt.user_id === val.user_id}
                disabled={!organizationId}
                renderOption={(props, option) => (
                  <li {...props} key={option.user_id}>
                    <Box>
                      <Typography variant="body2">{option.name ?? 'Unnamed'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.email ?? option.user_id}
                        {option.roles?.length ? ` · ${option.roles.join(', ')}` : ''}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Provider"
                    placeholder="Select a provider..."
                    size="small"
                    required
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {orgLoading && <CircularProgress size={18} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              {/* Encounter picker */}
              <Autocomplete
                options={encounters}
                loading={encountersLoading}
                value={selectedEncounter}
                onChange={(_e, value) => setSelectedEncounter(value)}
                getOptionLabel={encounterLabel}
                isOptionEqualToValue={(opt, val) => opt.encounter_id === val.encounter_id}
                disabled={!effectiveProviderId}
                renderOption={(props, option) => (
                  <li {...props} key={option.encounter_id}>
                    <Box sx={{ width: '100%' }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" fontWeight={500}>
                          {option.encounter_type ?? 'Unknown type'}
                        </Typography>
                        <Chip
                          label={option.status}
                          color={getStatusBadgeColor(option.status)}
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {option.created_at ? formatDateTime(option.created_at) : 'No date'}
                      </Typography>
                      {option.note_title && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Note: {option.note_title}
                        </Typography>
                      )}
                      {option.template_id && (
                        <Typography variant="caption" color="text.secondary" display="block" fontFamily="monospace" fontSize="0.7rem">
                          Template: {option.template_id.substring(0, 12)}...
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Encounter"
                    placeholder={effectiveProviderId ? 'Select an encounter...' : 'Select a provider first'}
                    size="small"
                    required
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {encountersLoading && <CircularProgress size={18} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              {createError && (
                <Alert severity="error">
                  Failed to trigger shadow note generation. Check that the encounter and org IDs are valid.
                </Alert>
              )}
            </Stack>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => void handleTrigger()}
              disabled={!canTrigger}
              startIcon={isCreating ? <CircularProgress size={16} color="inherit" /> : <Science />}
            >
              {isCreating ? 'Generating...' : 'Generate Shadow Note'}
            </Button>
          </DialogActions>
        </>
      )}

      {/* ---- VIEW PHASE ---- */}
      {isViewing && (
        <>
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Science color="primary" />
                  <Typography variant="h6">
                    {shadowNote?.note_title || 'Shadow Note'}
                  </Typography>
                  <Chip
                    label={status}
                    color={getStatusBadgeColor(status)}
                    size="small"
                  />
                  {status === 'processing' && <CircularProgress size={16} />}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Template: {templateTitle} (v{templateVersion})
                </Typography>
              </Box>
            </Box>
          </DialogTitle>

          <DialogContent dividers sx={{ overflow: 'auto' }}>
            {status === 'error' && shadowNote?.error_message && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {shadowNote.error_message}
              </Alert>
            )}

            {status === 'processing' && sections.length === 0 ? (
              <Box display="flex" alignItems="center" justifyContent="center" gap={2} py={8}>
                <CircularProgress size={24} />
                <Typography color="text.secondary">
                  Generating note sections...
                </Typography>
              </Box>
            ) : sections.length === 0 ? (
              <Typography color="text.secondary" textAlign="center" py={4}>
                No sections generated.
              </Typography>
            ) : (
              <Stack spacing={3}>
                {sections.map((section, index) => (
                  <Box key={section.section_id ?? index}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {section.name}
                    </Typography>
                    <MarkdownNoteSectionDisplayBox
                      text={section.edited_text || section.text || ''}
                    />
                  </Box>
                ))}
              </Stack>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose}>Close</Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
